/**
 * Home Lab — the direct focus-group launch (docs/plans/home-lab-direct-focus-group.md, Step 2).
 *
 * Draw the twins, create the group room, hand the token back. That is the whole module.
 *
 * ============================================================================
 * TEN SEATS, ON PURPOSE
 * ============================================================================
 *
 * The prior art is `jobs/chat/chatTransport.ts`, which seats FOUR and writes down why: the
 * room bills per message the user sends (`ROOM_CREDITS_PER_MSG`), and a ten-way room makes
 * every reply a wall of text to read turn by turn. That argument is sound for what it
 * describes, a one-to-few conversation the user drives.
 *
 * This module seats TEN anyway, because it is not that. A focus group is the product being
 * asked for here: the user wants a spread of reactions to one question, so a room of four
 * is a sample too thin to read anything off. The cost of the wider room is paid in reading
 * time and in the intro round before the opening question lands, not in a worse answer.
 *
 * The four-seat number is NOT a rule this file is breaking. It belongs to a different job
 * (the assistant's one-to-few chat hand-off) and is cited here only as prior art, so that a
 * later reader finds the argument rather than assuming the two numbers drifted apart by
 * accident. `chatTransport.ts` is not touched by this plan.
 *
 * ============================================================================
 * FAILURES ARE RETURNED, NOT THROWN
 * ============================================================================
 *
 * `chatTransport.ts` throws `createAssistantJobFailure` for the same refusals. That helper
 * belongs to the assistant job registry, which this plan is unhooking, so depending on it
 * here would be a new dependency on something on its way out. This module returns a
 * discriminated `FocusGroupLaunchResult` instead: the caller renders the reason inline next
 * to the composer, and there is no throw for it to remember to catch.
 *
 * It also does NOT navigate. The token comes back and the caller pushes
 * `ROUTES.chatRoom(token)`, so the route string is never built by hand.
 */
import {
  ChatRoomSettings,
  ConversationLevel,
  ConversationModel,
  CreateChatRoomDto,
  tagConditionsToFilters,
} from '@digital-twins/shared-types'

import { chatRoomsApi, populationsApi } from '@/lib/api/client'
import { blockedFeatureMessage } from '@/lib/blocked-feature-error'
import { detectTrialLimitError } from '@/lib/billing/trial-limits'
import i18n from '@/i18n'

import { HomeLabSegment } from '@/lib/home-lab/segment'

/** How many twins a fast start seats. See the header: this is a focus group, not a chat. */
export const FOCUS_GROUP_SEAT_COUNT = 10

/**
 * Below this, the room is not opened at all.
 *
 * A saved cut can legitimately match almost nobody, and a "group" of one is the failure this
 * guards: the user would land in a room, wait through one intro, and get a single opinion
 * presented as a focus group. Two is the smallest number that can still disagree.
 */
export const FOCUS_GROUP_MIN_SEATS = 2

/**
 * The Home chat composer's room choices (composer model levels plan, Step 5). Every field is
 * optional. The backend merges `DEFAULT_ROOM_SETTINGS` for every omitted field. The level is
 * always sent when present: an omitted level is stamped `DEFAULT_NEW_ROOM_LEVEL` (Medium) server-side, so an explicit Low
 * must ride along to survive.
 */
export interface FocusGroupLaunchOptions {
  /** Sent whenever present (omitted = the server stamps `DEFAULT_NEW_ROOM_LEVEL`). */
  conversationLevel?: ConversationLevel
  /** An advanced pick (entitled users only); sent whenever present. */
  conversationModel?: ConversationModel | null
}

/** The create payload's `settings`: the language, the level and the advanced pick. */
export function buildFocusGroupSettings(
  language: string | null,
  options: FocusGroupLaunchOptions,
): Partial<ChatRoomSettings> | null {
  const settings: Partial<ChatRoomSettings> = {}
  if (language) {
    settings.language = language
  }
  if (options.conversationLevel) {
    settings.conversationLevel = options.conversationLevel
  }
  if (options.conversationModel) {
    settings.conversationModel = options.conversationModel
  }
  return Object.keys(settings).length > 0 ? settings : null
}

export type FocusGroupLaunchFailureReason =
  | 'no_audience'
  | 'too_few_twins'
  | 'no_room_token'
  | 'trial_capped'
  | 'failed'

export interface FocusGroupLaunchSuccess {
  ok: true
  token: string
}

export interface FocusGroupLaunchFailure {
  ok: false
  reason: FocusGroupLaunchFailureReason
  /** Already user-facing. The caller renders it verbatim, it does not re-word by reason. */
  message: string
}

export type FocusGroupLaunchResult = FocusGroupLaunchSuccess | FocusGroupLaunchFailure

/**
 * Both API edges, injected. Same shape `chatTransport.ts` uses, widened by the optional
 * `filters` argument a cut needs.
 */
export interface FocusGroupLaunchDeps {
  getRandomPersonaIds: (
    audienceId: string,
    count: number,
    filters?: Record<string, Array<string>>,
  ) => Promise<{ personaIds: Array<string> }>
  createRoom: (dto: CreateChatRoomDto) => Promise<{ token: string }>
}

export function defaultFocusGroupLaunchDeps(): FocusGroupLaunchDeps {
  return {
    getRandomPersonaIds: (audienceId, count, filters) =>
      populationsApi.getRandomPersonaIds(audienceId, count, filters),
    createRoom: (dto) => chatRoomsApi.create(dto),
  }
}

function failure(
  reason: FocusGroupLaunchFailureReason,
  message: string,
): FocusGroupLaunchFailure {
  return { ok: false, reason, message }
}

/**
 * Turn a thrown API error into a named refusal.
 *
 * The trial branch mirrors `HomePage/HomeStage.tsx` (`detectTrialLimitError(err) === 'rooms'`
 * in its `handleStartChat` catch): a capped trial is not a broken request, it is an upgrade
 * prompt, and the caller opens `TrialLimitUpgradeModal` for it rather than printing a reason.
 */
function toFailure(error: unknown): FocusGroupLaunchFailure {
  if (detectTrialLimitError(error) === 'rooms') {
    return failure('trial_capped', i18n.t('homeLab.focusGroup.trialCapped', { ns: 'app' }))
  }
  const blocked = blockedFeatureMessage(error)
  if (blocked) {
    return failure('failed', blocked)
  }
  const detail = error instanceof Error ? error.message : ''
  return failure(
    'failed',
    detail.length > 0
      ? i18n.t('homeLab.focusGroup.startFailedWithDetail', { ns: 'app', detail })
      : i18n.t('homeLab.focusGroup.startFailed', { ns: 'app' }),
  )
}

/**
 * Seat a focus group over one segment and return the room token.
 *
 * A genpop segment (`tag === null`) draws unfiltered. A cut draws with exactly its tag's
 * conditions, mapped by the shared `tagConditionsToFilters`: the same mapping Quick Study
 * uses for the same tags, so the two surfaces cannot drift into drawing different people
 * from the same saved cut.
 *
 * `language` (Composer Language plan, D2) IS THE EFFECTIVE VALUE, already resolved by the
 * caller: `HomeLabStage` computes `override ?? segment?.language ?? null` and hands the
 * result straight through, so this function never reads `segment.language` itself and never
 * has to decide between an explicit pick and the segment's own language. See the comment on
 * the `createRoom` call below for what happens with it.
 */
export async function launchFocusGroup(
  segment: HomeLabSegment,
  language: string | null,
  options: FocusGroupLaunchOptions = {},
  deps: FocusGroupLaunchDeps = defaultFocusGroupLaunchDeps(),
): Promise<FocusGroupLaunchResult> {
  const audienceId = (segment?.audienceId ?? '').trim()
  if (audienceId.length === 0) {
    return failure('no_audience', i18n.t('homeLab.focusGroup.noAudience', { ns: 'app' }))
  }

  try {
    /* The third argument is OMITTED for genpop, never passed as an empty object: an empty
       filter map and no filter map are the same draw, and the omission is what the test
       pins so a later refactor cannot quietly start sending `{}` per row. */
    const drawn = segment.tag
      ? await deps.getRandomPersonaIds(
          audienceId,
          FOCUS_GROUP_SEAT_COUNT,
          tagConditionsToFilters(segment.tag),
        )
      : await deps.getRandomPersonaIds(audienceId, FOCUS_GROUP_SEAT_COUNT)

    const participantPersonaIds = (drawn?.personaIds ?? []).filter(
      (personaId) => typeof personaId === 'string' && personaId.trim().length > 0,
    )
    if (participantPersonaIds.length < FOCUS_GROUP_MIN_SEATS) {
      return failure(
        'too_few_twins',
        i18n.t('homeLab.focusGroup.tooFewTwins', { ns: 'app', label: segment.label }),
      )
    }

    /* `settings.language` rides along ONLY when `language` is non-null (Composer Language
       plan, D2). The backend merges `DEFAULT_ROOM_SETTINGS` for every field a caller omits,
       and inventing a language would still be worse than omitting one: that is exactly what
       a null here means, since it is a real "nothing resolved" (no explicit pick, no
       audience language known), not a placeholder for `'en'`. It stays true because a
       non-null value is never invented by this function either — it is either the user's own
       explicit pick or the audience's own language (RND-2668), carried in by the caller, so
       passing it through is not the guess the blanket omission used to avoid. Same
       `settings: { language }` call shape `useComposeModal.ts:736` documents. */
    /* The room choices (`options`): the level and an advanced pick ride along when
       present, see `buildFocusGroupSettings`. */
    const settings = buildFocusGroupSettings(language, options)
    const room = await deps.createRoom({
      populationId: audienceId,
      participantPersonaIds,
      ...(settings ? { settings } : {}),
    })
    const token = (room?.token ?? '').trim()
    if (token.length === 0) {
      return failure('no_room_token', i18n.t('homeLab.focusGroup.noRoomToken', { ns: 'app' }))
    }

    return { ok: true, token }
  } catch (error) {
    return toFailure(error)
  }
}
